import React, { useState, useEffect, useMemo, useRef } from "react";
import forms from "./plans/form.js";
import { submitAssessmentForm } from "../../services/assessmentService";
import { generateCompleteCreditPlan } from "../../services/creditPlanService";
import { useAuth } from "../auth/AuthContext";
import { useScope } from "../../hooks/useScope";
import { useTranslation } from "../../hooks/useTranslation";
import { translateFormConfig } from "../../utils/translateFormConfig";
import {
  trackFeatureUsed,
  trackCTAClick,
} from "../../services/analyticsService";
import MarkdownRenderer from "./MarkdownRenderer";
import { useRateLimitedFunction } from "../../hooks/useRateLimiter";
import DisclaimerModal from "./DisclaimerModal";
import Modal from "../common/Modal";
import "./AssessmentForm.css";

// Word limit for assessment form text fields to prevent abuse
const ASSESSMENT_WORD_LIMIT = 500;

const AssessmentForm = ({ formId, subcategory, goal, onClose, onSubmit }) => {
  const { currentUser } = useAuth();
  const { isManaged, isClientView, mentorId, effectiveUserId, actualUserId } =
    useScope();
  const t = useTranslation();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [creditPlan, setCreditPlan] = useState(null);
  const [generationProgress, setGenerationProgress] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  // Rate limit: 3 assessments per minute to prevent spam
  const {
    execute: submitWithRateLimit,
    getRemainingCalls,
    isRateLimited,
  } = useRateLimitedFunction(
    async (data) => await submitAssessmentForm(data),
    3,
    60000
  );

  // Get form configuration based on subcategory and goal
  const getFormConfig = () => {
    // First, try to find a goal-specific form based on the goal text
    let formConfig = null;

    // Create a mapping from goal text to form keys
    const goalToFormMapping = {
      "Create 75-90 Day Payment Cycles": "create_75_90_day_payment_cycles",
      "Eliminate Interest Payments": "eliminate_interest_payments",
      "Generate $300-600 Monthly Banking Bonuses": "generate_banking_bonuses",
      "Fund Initial Inventory/Operations": "fund_initial_inventory_operations",
      "Scale Business Operations": "scale_business_operations",
      "Separate Business and Personal Finances":
        "separate_business_personal_finances",
      "Maintain Clean Personal Credit": "maintain_clean_personal_credit",
      "Create Multiple Funding Sources": "create_multiple_funding_sources",
      "Establish Emergency Capital Access":
        "establish_emergency_capital_access",
      "Build Recession-Proof Funding": "build_recession_proof_funding",
      "Establish Elite Banking Relationships":
        "establish_elite_banking_relationships",
      "Build Business Credit History": "build_business_credit_history",
      "Create Vendor/Supplier Leverage": "create_vendor_supplier_leverage",
    };

    // Create a mapping from subcategory to form keys
    const subcategoryToFormMapping = {
      personal_asset_protection: "risk_mitigation",
      financial_flexibility: "create_multiple_funding_sources",
      professional_relationship_building:
        "establish_elite_banking_relationships",
    };

    // Try to find a specific form for this goal
    if (goal && goal.goal && goalToFormMapping[goal.goal]) {
      const specificFormKey = goalToFormMapping[goal.goal];
      formConfig = forms.high_level_goal_forms[specificFormKey];
      if (formConfig) {
        console.log(`Found specific form for goal: ${goal.goal}`);
        return formConfig;
      }
    }

    // Try to find a form based on subcategory mapping
    if (subcategory && subcategoryToFormMapping[subcategory]) {
      const mappedFormKey = subcategoryToFormMapping[subcategory];
      formConfig = forms.high_level_goal_forms[mappedFormKey];
      if (formConfig) {
        console.log(
          `Found mapped form for subcategory: ${subcategory} -> ${mappedFormKey}`
        );
        return formConfig;
      }
    }

    // Fall back to subcategory form
    formConfig = forms.high_level_goal_forms[subcategory];
    if (!formConfig) {
      console.error(
        `Form configuration not found for subcategory: ${subcategory}`
      );
      return null;
    }

    console.log(`Using subcategory form: ${subcategory}`);
    return formConfig;
  };

  // Memoize raw form config to prevent infinite loops
  const rawFormConfig = useMemo(() => {
    return getFormConfig();
  }, [subcategory, goal]);

  // Translate form config - use rawFormConfig as the key to prevent unnecessary recalculations
  const formConfig = useMemo(() => {
    if (!rawFormConfig) return null;
    return translateFormConfig(rawFormConfig, t);
  }, [rawFormConfig, t]);

  // Use a ref to track the last initialized form config to prevent infinite loops
  const initializedConfigRef = useRef(null);

  // Helper function to count words in text
  const countWords = (text) => {
    if (!text || !text.trim()) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  // Initialize form data with empty values
  useEffect(() => {
    // Create a stable key for this form config
    const configKey = rawFormConfig
      ? `${subcategory}-${goal?.id || "no-goal"}`
      : null;

    // Only initialize if we have a config and haven't initialized for this specific config yet
    // Use rawFormConfig.input_fields since field structure doesn't change with translation
    if (
      rawFormConfig &&
      configKey &&
      initializedConfigRef.current !== configKey
    ) {
      const initialData = {};
      rawFormConfig.input_fields.forEach((field) => {
        if (field.field_type === "multiselect") {
          initialData[field.field_id] = [];
        } else {
          initialData[field.field_id] = "";
        }
      });
      setFormData(initialData);
      initializedConfigRef.current = configKey;

      // Track form open
      trackFeatureUsed({
        section: "actionable_plans",
        feature: "assessment_form",
        action: "open",
        source: "modal",
        additionalParams: {
          form_id: formId,
          subcategory: subcategory,
          goal_id: goal?.id,
          goal_title: goal?.goal,
        },
      });
    }
  }, [rawFormConfig, subcategory, goal, formId]);

  // Handle input changes
  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors((prev) => ({
        ...prev,
        [fieldId]: "",
      }));
    }
  };

  // Handle multiselect changes
  const handleMultiselectChange = (fieldId, option) => {
    setFormData((prev) => {
      const currentValues = prev[fieldId] || [];
      const newValues = currentValues.includes(option)
        ? currentValues.filter((item) => item !== option)
        : [...currentValues, option];

      return {
        ...prev,
        [fieldId]: newValues,
      };
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    formConfig.input_fields.forEach((field) => {
      const value = formData[field.field_id];

      // Check required fields
      if (
        field.required &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        newErrors[field.field_id] = `${field.label} is required`;
        return;
      }

      // Check validation rules
      if (field.validation && value) {
        if (
          field.validation.min !== undefined &&
          Number(value) < field.validation.min
        ) {
          newErrors[
            field.field_id
          ] = `Value must be at least ${field.validation.min}`;
        }
        if (
          field.validation.max !== undefined &&
          Number(value) > field.validation.max
        ) {
          newErrors[
            field.field_id
          ] = `Value must be no more than ${field.validation.max}`;
        }
      }

      // Check word limit for text and textarea fields
      if (
        (field.field_type === "text" || field.field_type === "textarea") &&
        value
      ) {
        const wordCount = countWords(value);
        if (wordCount > ASSESSMENT_WORD_LIMIT) {
          newErrors[
            field.field_id
          ] = `This field exceeds the word limit of ${ASSESSMENT_WORD_LIMIT} words. Please shorten your response.`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Track validation error
      trackFeatureUsed({
        section: "actionable_plans",
        feature: "assessment_form",
        action: "validation_error",
        source: "form",
        additionalParams: {
          form_id: formId,
          subcategory: subcategory,
        },
      });
      return;
    }

    // Check if disclaimer has been accepted
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
      return;
    }

    // Track form submit start
    trackFeatureUsed({
      section: "actionable_plans",
      feature: "assessment_form",
      action: "submit",
      source: "form",
      additionalParams: {
        form_id: formId,
        subcategory: subcategory,
        goal_id: goal?.id,
        goal_title: goal?.goal,
        field_count: Object.keys(formData).length,
      },
    });

    setIsSubmitting(true);
    setGenerationProgress(t("assessmentForm.generatingPlan"));

    try {
      // Build scope data for mentor masquerade mode
      // isMentorMasquerade: mentor is viewing client's data (isManaged && !isClientView)
      const isMentorMasquerade = isManaged && !isClientView;

      const submissionData = {
        formId,
        subcategory,
        goal,
        formData,
        submittedAt: new Date().toISOString(),
        // Scope data for mentor/client association
        ...(isMentorMasquerade && effectiveUserId && actualUserId
          ? {
              scopeData: {
                clientUserId: effectiveUserId,
                mentorId: actualUserId,
                isMentorCreated: true,
              },
            }
          : {}),
      };

      console.log("Submitting assessment to Firebase:", submissionData);

      // First, submit the assessment for basic analysis (with rate limiting)
      const result = await submitWithRateLimit(submissionData);
      console.log("AI Analysis received:", result);
      setAiAnalysis(result.data.aiAnalysis);

      // Then generate the complete credit plan
      // Note: Credit is consumed automatically on the backend after successful plan generation
      setGenerationProgress(t("assessmentForm.phase1"));
      const planResult = await generateCompleteCreditPlan(
        submissionData,
        result.data.aiAnalysis,
        setGenerationProgress
      );
      console.log("Credit plan generated:", planResult);
      setGenerationProgress("Credit plan generated:", planResult);

      setCreditPlan(planResult);
      setGenerationProgress(t("assessmentForm.planGenerated"));
      setShowResults(true);

      // Track successful plan generation
      trackFeatureUsed({
        section: "actionable_plans",
        feature: "assessment_form",
        action: "plan_generated",
        source: "form",
        additionalParams: {
          form_id: formId,
          subcategory: subcategory,
          goal_id: goal?.id,
          plan_steps: planResult?.planStructure
            ? Object.keys(planResult.planStructure).length - 1
            : 0,
        },
      });

      if (onSubmit) {
        onSubmit({
          ...submissionData,
          creditPlan: planResult,
        });
      }
    } catch (error) {
      console.error("Assessment submission error:", error);

      // Check if it's a rate limit error
      if (error.rateLimited) {
        const seconds = Math.ceil(error.retryAfter / 1000);
        setErrorModal({
          show: true,
          type: "warning",
          title: t("assessmentForm.rateLimitTitle", "Rate Limit Exceeded"),
          message: t("assessmentForm.rateLimitExceeded").replace(
            "{seconds}",
            seconds.toString()
          ),
        });
      } else {
        setErrorModal({
          show: true,
          type: "error",
          title: t("assessmentForm.errorTitle", "Submission Error"),
          message: t("assessmentForm.errorSubmitting").replace(
            "{error}",
            error.message
          ),
        });
      }

      // Track error
      trackFeatureUsed({
        section: "actionable_plans",
        feature: "assessment_form",
        action: error.rateLimited ? "rate_limited" : "error",
        source: "form",
        additionalParams: {
          form_id: formId,
          subcategory: subcategory,
          error: error.message,
          remaining_calls: getRemainingCalls(),
        },
      });
    } finally {
      setIsSubmitting(false);
      setGenerationProgress("");
    }
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);

    // Track form submit start
    trackFeatureUsed({
      section: "actionable_plans",
      feature: "assessment_form",
      action: "submit",
      source: "form",
      additionalParams: {
        form_id: formId,
        subcategory: subcategory,
        goal_id: goal?.id,
        goal_title: goal?.goal,
        field_count: Object.keys(formData).length,
      },
    });

    setIsSubmitting(true);
    setGenerationProgress(t("assessmentForm.generatingPlan"));

    try {
      // Build scope data for mentor masquerade mode
      // isMentorMasquerade: mentor is viewing client's data (isManaged && !isClientView)
      const isMentorMasquerade = isManaged && !isClientView;

      const submissionData = {
        formId,
        subcategory,
        goal,
        formData,
        submittedAt: new Date().toISOString(),
        // Scope data for mentor/client association
        ...(isMentorMasquerade && effectiveUserId && actualUserId
          ? {
              scopeData: {
                clientUserId: effectiveUserId,
                mentorId: actualUserId,
                isMentorCreated: true,
              },
            }
          : {}),
      };

      console.log("Submitting assessment to Firebase:", submissionData);

      // First, submit the assessment for basic analysis (with rate limiting)
      const result = await submitWithRateLimit(submissionData);
      console.log("AI Analysis received:", result);
      setAiAnalysis(result.data.aiAnalysis);

      // Then generate the complete credit plan
      // Note: Credit is consumed automatically on the backend after successful plan generation
      setGenerationProgress(t("assessmentForm.phase1"));
      const planResult = await generateCompleteCreditPlan(
        submissionData,
        result.data.aiAnalysis,
        setGenerationProgress
      );
      console.log("Credit plan generated:", planResult);
      setGenerationProgress("Credit plan generated:", planResult);

      setCreditPlan(planResult);
      setGenerationProgress(t("assessmentForm.planGenerated"));
      setShowResults(true);

      // Track successful plan generation
      trackFeatureUsed({
        section: "actionable_plans",
        feature: "assessment_form",
        action: "plan_generated",
        source: "form",
        additionalParams: {
          form_id: formId,
          subcategory: subcategory,
          goal_id: goal?.id,
          plan_steps: planResult?.planStructure
            ? Object.keys(planResult.planStructure).length - 1
            : 0,
        },
      });

      if (onSubmit) {
        onSubmit({
          ...submissionData,
          creditPlan: planResult,
        });
      }
    } catch (error) {
      console.error("Assessment submission error:", error);

      // Check if it's a rate limit error
      if (error.rateLimited) {
        const seconds = Math.ceil(error.retryAfter / 1000);
        setErrorModal({
          show: true,
          type: "warning",
          title: t("assessmentForm.rateLimitTitle", "Rate Limit Exceeded"),
          message: t("assessmentForm.rateLimitExceeded").replace(
            "{seconds}",
            seconds.toString()
          ),
        });
      } else {
        setErrorModal({
          show: true,
          type: "error",
          title: t("assessmentForm.errorTitle", "Submission Error"),
          message: t("assessmentForm.errorSubmitting").replace(
            "{error}",
            error.message
          ),
        });
      }

      // Track error
      trackFeatureUsed({
        section: "actionable_plans",
        feature: "assessment_form",
        action: "error",
        source: "form",
        additionalParams: {
          form_id: formId,
          subcategory: subcategory,
          error_type: error.rateLimited ? "rate_limit" : "submission_error",
          error_message: error.message,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
    // Track disclaimer rejection
    trackFeatureUsed({
      section: "actionable_plans",
      feature: "assessment_form",
      action: "disclaimer_rejected",
      source: "modal",
      additionalParams: {
        form_id: formId,
        subcategory: subcategory,
      },
    });
  };

  // Render different field types
  const renderField = (field) => {
    const value = formData[field.field_id] || "";
    const error = errors[field.field_id];

    switch (field.field_type) {
      case "number":
        return (
          <input
            type="number"
            id={field.field_id}
            value={value}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
            className={`form-input ${error ? "error" : ""}`}
          />
        );

      case "text":
        const textWordCount = countWords(value);
        return (
          <div className="form-input-wrapper">
            <input
              type="text"
              id={field.field_id}
              value={value}
              onChange={(e) =>
                handleInputChange(field.field_id, e.target.value)
              }
              placeholder={field.placeholder}
              className={`form-input ${error ? "error" : ""}`}
              maxLength={3000}
            />
            <div
              className={`form-word-count ${
                textWordCount > ASSESSMENT_WORD_LIMIT
                  ? "form-word-count-error"
                  : textWordCount > ASSESSMENT_WORD_LIMIT * 0.9
                  ? "form-word-count-warning"
                  : ""
              }`}
            >
              {t("assessmentForm.words")
                .replace("{count}", textWordCount.toString())
                .replace("{limit}", ASSESSMENT_WORD_LIMIT.toString())}
            </div>
          </div>
        );

      case "select":
        return (
          <select
            id={field.field_id}
            value={value}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            className={`form-select ${error ? "error" : ""}`}
          >
            <option value="">{t("assessmentForm.selectOption")}</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "multiselect":
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="multiselect-container">
            {field.options.map((option, index) => (
              <label key={index} className="multiselect-option">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={() =>
                    handleMultiselectChange(field.field_id, option)
                  }
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case "textarea":
        const textareaWordCount = countWords(value);
        return (
          <div className="form-textarea-wrapper">
            <textarea
              id={field.field_id}
              value={value}
              onChange={(e) =>
                handleInputChange(field.field_id, e.target.value)
              }
              placeholder={field.placeholder}
              rows={4}
              className={`form-textarea ${error ? "error" : ""}`}
              maxLength={3000}
            />
            <div
              className={`form-word-count ${
                textareaWordCount > ASSESSMENT_WORD_LIMIT
                  ? "form-word-count-error"
                  : textareaWordCount > ASSESSMENT_WORD_LIMIT * 0.9
                  ? "form-word-count-warning"
                  : ""
              }`}
            >
              {t("assessmentForm.words")
                .replace("{count}", textareaWordCount.toString())
                .replace("{limit}", ASSESSMENT_WORD_LIMIT.toString())}
            </div>
          </div>
        );

      default:
        return (
          <div>
            {t("assessmentForm.unsupportedFieldType").replace(
              "{type}",
              field.field_type
            )}
          </div>
        );
    }
  };

  if (!formConfig) {
    return (
      <div
        className="assessment-form-overlay"
        onClick={() => {
          trackFeatureUsed({
            section: "actionable_plans",
            feature: "assessment_form",
            action: "close",
            source: "overlay",
          });
          onClose();
        }}
      >
        <div
          className="assessment-form-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="form-error">
            <h2>{t("assessmentForm.formNotFound")}</h2>
            <p>
              {t("assessmentForm.formNotFoundMessage").replace(
                "{subcategory}",
                subcategory
              )}
            </p>
            <button
              onClick={() => {
                trackFeatureUsed({
                  section: "actionable_plans",
                  feature: "assessment_form",
                  action: "close",
                  source: "button",
                });
                onClose();
              }}
              className="btn-secondary"
            >
              {t("assessmentForm.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="assessment-form-overlay"
      onClick={() => {
        trackFeatureUsed({
          section: "actionable_plans",
          feature: "assessment_form",
          action: "close",
          source: "overlay",
        });
        onClose();
      }}
    >
      <div
        className="assessment-form-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-header">
          <h2>{formConfig.form_title}</h2>
          <p>{formConfig.form_description}</p>
          {goal && (
            <div className="goal-context">
              <h3>
                {t("assessmentForm.assessmentFor")} {goal.goal}
              </h3>
              <p>
                {t("assessmentForm.timeline")} {goal.timeline}
              </p>
            </div>
          )}
          <button
            onClick={() => {
              trackFeatureUsed({
                section: "actionable_plans",
                feature: "assessment_form",
                action: "close",
                source: "close_button",
              });
              onClose();
            }}
            className="close-button"
          >
            ×
          </button>
        </div>

        {!showResults && !aiAnalysis && (
          <form onSubmit={handleSubmit} className="assessment-form">
            <div className="form-fields">
              {formConfig.input_fields.map((field) => (
                <div key={field.field_id} className="form-field">
                  <label htmlFor={field.field_id} className="field-label">
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </label>

                  {renderField(field)}

                  {errors[field.field_id] && (
                    <div className="field-error">{errors[field.field_id]}</div>
                  )}
                </div>
              ))}
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => {
                  trackFeatureUsed({
                    section: "actionable_plans",
                    feature: "assessment_form",
                    action: "cancel",
                    source: "button",
                    additionalParams: {
                      form_id: formId,
                      subcategory: subcategory,
                    },
                  });
                  onClose();
                }}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                {t("assessmentForm.cancel")}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t("assessmentForm.submitting")
                  : t("assessmentForm.submitAssessment")}
              </button>
            </div>
          </form>
        )}

        {/* Progress Indicator */}
        {isSubmitting && generationProgress && (
          <div className="generation-progress">
            <div className="progress-content">
              <h3>{t("assessmentForm.generatingCreditPlan")}</h3>
              <p>{t("assessmentForm.generatingMessage")}</p>
              <p>{generationProgress}</p>
              <div className="progress-spinner"></div>
            </div>
          </div>
        )}

        {/* AI Analysis Results */}
        {showResults && aiAnalysis && (
          <div className="ai-analysis-results">
            <div className="results-header">
              <h3>{t("assessmentForm.aiAnalysisRecommendations")}</h3>
              <button
                onClick={() => setShowResults(false)}
                className="close-results"
              >
                ×
              </button>
            </div>
            <div className="analysis-content">
              <div className="analysis-text">
                <MarkdownRenderer
                  content={aiAnalysis}
                  className="analysis-markdown"
                  showFullText={showFullAnalysis}
                  onToggleText={() => setShowFullAnalysis(!showFullAnalysis)}
                  showToggleButton={true}
                  showMoreText={t("assessmentForm.showMore")}
                  showLessText={t("assessmentForm.showLess")}
                />
              </div>
            </div>

            {/* Credit Plan Results */}
            {creditPlan && (
              <div className="credit-plan-results">
                <div className="plan-header">
                  <h3>{t("assessmentForm.personalizedCreditPlan")}</h3>
                  <p>
                    {t("assessmentForm.generatedSteps").replace(
                      "{count}",
                      (creditPlan.planStructure
                        ? Object.keys(creditPlan.planStructure).length - 1
                        : 0
                      ).toString()
                    )}
                  </p>
                </div>
                <div className="plan-summary">
                  <div className="plan-stats">
                    <div className="stat">
                      <span className="stat-label-form">
                        {t("assessmentForm.goalLabel")}
                      </span>
                      <span className="stat-value">{creditPlan.goal.goal}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label-form">
                        {t("assessmentForm.timelineLabel")}
                      </span>
                      <span className="stat-value">
                        {creditPlan.goal.timeline}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="plan-actions">
                  <button
                    onClick={() => {
                      // Track navigation to plan
                      trackCTAClick({
                        section: "actionable_plans",
                        action: "view_complete_plan",
                        source: "button",
                        additionalParams: {
                          form_id: formId,
                          goal_id: goal?.id,
                        },
                      });

                      // Navigate to stored assessment history
                      window.location.href = "/history";
                    }}
                    className="btn-primary"
                  >
                    {t("assessmentForm.viewCompletePlan")}
                  </button>
                </div>
              </div>
            )}

            {!showResults && !aiAnalysis && (
              <div className="results-actions">
                <button
                  onClick={() => {
                    trackFeatureUsed({
                      section: "actionable_plans",
                      feature: "assessment_form",
                      action: "close",
                      source: "button",
                      additionalParams: {
                        form_id: formId,
                        subcategory: subcategory,
                      },
                    });
                    onClose();
                  }}
                  className="btn-primary"
                >
                  {t("assessmentForm.closeAssessment")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleDisclaimerAccept}
        onCancel={handleDisclaimerCancel}
      />

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, message: "" })}
        type={errorModal.type || "error"}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

export default AssessmentForm;
